import { useTranslation } from 'react-i18next';
import { useLocation, Outlet } from 'react-router-dom';
import AssetCard from '../../components/assetCard/AssetCard';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAssetsForBrowser } from '../../services/AssetBrowserAPI';
import logo from '../../assets/deviceImages/4500.png';
import './AssetDashboard.css';
import Sidebar from '../../components/sidebar/Sidebar';
import assetMenu from '../../utils/assetMenu';
import Error404 from '../error404/Error404';
import { formatToBrowserTime } from '../../utils/timeFormat';
import { muteDevice, unmuteDevice } from '../../services/AssetBrowserAPI';

const AssetDashboard = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const formatResponse = (dataArray) =>
        dataArray.map((item) => ({
            status: item.connection_status,
            modelNumber: item.parameters?.DeviceModel ?? item.analyzer_model ?? '',
            serialNumber: item.parameters?.DeviceSerial ?? item.system_serial_number ?? '',
            location: item.di_location,
            status_update_date: item.status_update_date,
            muted: item.muted,
        }));

    const { model, serialNumber } = useMemo(() => {
        const sp = new URLSearchParams(location.search);
        return {
            model: sp.get('model') || '',
            serialNumber: sp.get('serialNumber') || '',
        };
    }, [location.search]);

    const loadData = useCallback(
        async (sData) => {
            try {
                setLoading(true);
                const raw = await getAssetsForBrowser(sData);
                const formatted = Array.isArray(raw) ? raw : [raw];
                const mapped = formatResponse(formatted);
                setData(mapped);
                setError(null);
            } catch (err) {
                setError(err.message || 'Unknown error');
                console.error('Fetch failed:', err);
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const refreshSilently = useCallback(
        async (sData) => {
            try {
                const raw = await getAssetsForBrowser(sData);
                const formatted = Array.isArray(raw) ? raw : [raw];
                const mapped = formatResponse(formatted);
                setData(mapped); // Just update; no spinner
            } catch (err) {
                console.error('Silent refresh failed:', err);
            }
        },
        []
    );

    const handleMuteToggle = async (action) => {
        if (!asset?.serialNumber) return;

        try {
            if (action === 'mute') {
                await muteDevice({ model, serial: serialNumber });
            } else if (action === 'unmute') {
                await unmuteDevice({ model, serial: serialNumber });
            }

            // refresh data after action
            await refreshSilently({ model, serial: serialNumber });
        } catch (err) {
            console.error('Mute/Unmute failed', err);
            throw err;
        }
    };

    useEffect(() => {
        const searchData = {};
        searchData.model = model;
        searchData.serial = serialNumber;
        loadData(searchData);
    }, [model, serialNumber]);

    useEffect(() => {
        if (!model && !serialNumber) return;

        const intervalId = setInterval(() => {
            const searchData = { model, serial: serialNumber };
            refreshSilently(searchData);
        }, 30_000);

        return () => clearInterval(intervalId);
    }, [model, serialNumber, refreshSilently]);

    // Redirect to home if fetch finished and no data came back
    if (!loading && !error && (data.length === 0 || data.length > 1)) {
        return <Error404 />;
    }

    const asset = data?.[0];

    return (
        <div className="dashboard-page-wrapper bg-light">
            <div className="dashboard-header">
                <h4>{t('Asset Dashboard')}</h4>
            </div>

            <div className="dashboard-content">
                <div>
                    <AssetCard
                        serialNumber={asset?.serialNumber || undefined}
                        model={asset?.modelNumber || undefined}
                        status={asset?.status}
                        muted={asset?.muted || ''}
                        registration={asset?.status_update_date ? formatToBrowserTime(asset.status_update_date, false, false) : undefined}
                        locationString={asset?.location || undefined}
                        imageSrc={logo}
                        loading={loading}
                        onMuteToggle={handleMuteToggle}
                    />
                </div>
                <div className="ad-body">
                    <Sidebar items={assetMenu} preserveSearch defaultCollapsed={false} />
                    <main className="ad-content">
                        <Outlet context={{ model, serialNumber }} />
                    </main>
                </div>
            </div>
        </div>
    );
};

export default AssetDashboard;
